import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ArticleCard from "@/components/feature/ArticleCard";
import SectionHeading from "@/components/feature/SectionHeading";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import RankingList from "@/pages/home/components/RankingList";
import CategoryQuickLinks from "@/pages/home/components/CategoryQuickLinks";
import LatestSection from "@/pages/home/components/LatestSection";
import HotVideosSection from "@/pages/home/components/HotVideosSection";
import TopicsSection from "@/pages/home/components/TopicsSection";
import { articles, latestArticles, popularArticles, topics, videos } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

export default function Home() {
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useSeo({
    title: "抖阳 - 专注原创内容与多元资讯的综合平台",
    description:
      "抖阳为用户提供原创文章、视频内容和多领域资讯，涵盖文化、娱乐及生活等多个类别，助力用户全面了解最新动态及深度信息。",
    keywords: "抖阳,原创内容,文化资讯,娱乐动态,生活分享,综合平台,多元内容,资讯门户",
  });

  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 450);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (location.hash !== "#topics") return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById("topics")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const featured = articles.filter((item) => item.featured).slice(0, 4);
  const ranked = popularArticles(5);
  const latest = latestArticles(6);
  const editorPicks = latestArticles(9).filter((item) => !item.featured).slice(0, 3);
  const hotVideos = videos.slice(0, 4);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section aria-label="焦点内容与热榜" className="px-4 pb-8 pt-6 md:px-6 md:pb-10 md:pt-8">
          <div className="mx-auto max-w-[1400px]">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                  抖阳 · 今日内容
                </h1>
                <p className="mt-1.5 text-sm text-foreground-500">
                  专注原创内容与多元资讯，覆盖文化、娱乐、生活与观点。
                </p>
              </div>
              <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.18em] text-foreground-400">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary-500" />
                Updated daily · 每日更新
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.9fr_1fr]">
              <HeroCarousel slides={featured} />
              <div className="hidden lg:block">
                <RankingList articles={ranked} title="24 小时热读榜" description="按阅读量实时排序，每两小时更新。" />
              </div>
            </div>
          </div>
        </section>

        <section aria-label="分类快捷入口" className="border-y border-background-200 bg-background-100/60 px-4 py-8 md:px-6 md:py-10">
          <div className="mx-auto max-w-[1400px]">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-heading text-base font-bold text-foreground-950 md:text-lg">按兴趣进入分类</h2>
              <p className="text-xs text-foreground-500">四个频道，每周固定更新</p>
            </div>
            <CategoryQuickLinks />
          </div>
        </section>

        <div className="mx-auto max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-8">
              <LatestSection articles={latest} loading={loading} />
            </div>
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <div className="mb-4 flex items-center justify-between border-b border-background-200 pb-3">
                  <h2 className="font-heading text-base font-bold text-foreground-950">编辑推荐</h2>
                  <span className="font-label text-[10px] uppercase tracking-[0.2em] text-foreground-400">
                    Editor&apos;s pick
                  </span>
                </div>
                <div className="space-y-3">
                  {editorPicks.map((article) => (
                    <ArticleCard key={article.id} article={article} variant="horizontal" showSummary={false} />
                  ))}
                </div>
                <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50/70 p-5">
                  <p className="font-label text-[10px] uppercase tracking-[0.2em] text-primary-700">Weekly Note</p>
                  <h3 className="mt-2 font-heading text-base font-bold text-foreground-950">本周编辑手记</h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                    「我们这周花最多时间的，是一条关于短剧片场的稿子。它不好写，因为没有人愿意承认自己在赶工。但正是这些细节，构成了内容行业的真实成本。」
                  </p>
                  <p className="mt-3 text-[11px] text-foreground-400">—— 主编 陈默</p>
                  <Link
                    to="/about"
                    className="mt-4 flex cursor-pointer items-center gap-1.5 text-xs text-primary-700 hover:text-primary-800"
                  >
                    了解我们的编辑原则
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] px-4 md:px-6">
          <HotVideosSection videos={hotVideos} />
        </div>

        <div className="mx-auto max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
          <TopicsSection topics={topics} />
        </div>

        <section aria-label="关于抖阳" className="border-t border-background-200 bg-background-100/70 px-4 py-10 md:px-6 md:py-14">
          <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-8 md:grid-cols-2 md:items-center">
            <div>
              <p className="font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">About Duyang</p>
              <h2 className="mt-2 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                我们相信，慢一点的内容
                <br className="hidden md:block" />
                会被记得更久一点
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-600">
                抖阳成立于 2023 年，编辑部由记者、编辑与视频创作者组成。我们不追逐每一个热搜，但会为一条值得的线索花上三周。所有内容均标注作者与来源，错误会在发现后第一时间更正。
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/about"
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  关于我们
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/contact"
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  联系与投稿
                  <i className="ri-mail-line" />
                </Link>
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-4">
              {[
                { label: "原创报道", value: "486", suffix: "篇" },
                { label: "原创视频", value: "132", suffix: "期" },
                { label: "签约作者", value: "37", suffix: "位", highlight: true },
                { label: "专题策划", value: "24", suffix: "个" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className={`rounded-lg border p-4 md:p-5 ${
                    stat.highlight ? "border-accent-200 bg-accent-50" : "border-background-200 bg-background-50"
                  }`}
                >
                  <dt className={`text-xs ${stat.highlight ? "text-accent-800" : "text-foreground-500"}`}>
                    {stat.label}
                  </dt>
                  <dd className="mt-2 flex items-baseline gap-1">
                    <span
                      className={`font-editorial text-2xl font-bold md:text-3xl ${
                        stat.highlight ? "text-accent-700" : "text-foreground-950"
                      }`}
                    >
                      {stat.value}
                    </span>
                    <span className={`text-xs ${stat.highlight ? "text-accent-700" : "text-foreground-400"}`}>
                      {stat.suffix}
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}